import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import SearchResultsToolbarActions from './SearchProductToolbarActions';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';

interface SortOption {
  value: string;
  label: string;
}

const searchSortOptions: SortOption[] = [
  { value: 'itemNum-asc', label: 'Item # - Ascending' },
  { value: 'itemNum-desc', label: 'Item # - Descending' },
  { value: 'description-asc', label: 'Description - Ascending' },
  { value: 'description-desc', label: 'Description - Descending' },
  { value: 'price-asc', label: 'Price - Ascending' },
  { value: 'price-desc', label: 'Price - Descending' },
  { value: 'brandName-asc', label: 'Brand - Ascending' },
  { value: 'brandName-desc', label: 'Brand - Descending' },
  { value: 'uom-asc', label: 'UOM - Ascending' },
  { value: 'uom-desc', label: 'UOM - Descending' },
  { value: 'catalogName-asc', label: 'Catalog - Ascending' },
  { value: 'catalogName-desc', label: 'Catalog - Descending' },
];

const getSortField = (value: string): string => {
  if (value.startsWith('itemNum-')) return 'itemNum';
  if (value.startsWith('description-')) return 'description';
  if (value.startsWith('price-')) return 'price';
  if (value.startsWith('brandName-')) return 'brandName';
  if (value.startsWith('uom-')) return 'uom';
  if (value.startsWith('catalogName-')) return 'catalogName';
  return 'itemNum';
};

const getSortDir = (value: string): 'asc' | 'desc' => (value.endsWith('-asc') ? 'asc' : 'desc');

const SearchCardGridHeader: FC = () => {
  const { t } = useTranslation();
  const dataState = useContextSelector(SearchProductFiltersContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.onDataStateChange
  );

  const translatedSortOptions = useMemo(
    () => searchSortOptions.map((opt) => ({ ...opt, label: t(opt.label) })),
    [t]
  );

  const selectedSort = useMemo(() => {
    const [defaultOption] = translatedSortOptions;
    if (!dataState?.sort || dataState.sort.length === 0) {
      return defaultOption;
    }
    const [currentSort] = dataState.sort;
    const { field, dir = 'asc' } = currentSort;
    const value = `${field}-${dir}`;
    return translatedSortOptions.find((opt) => opt.value === value) ?? defaultOption;
  }, [dataState?.sort, translatedSortOptions]);

  const handleSortChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const sortOption = e.value as SortOption;
      const field = getSortField(sortOption.value);
      const dir = getSortDir(sortOption.value);

      onDataStateChange({
        ...dataState,
        sort: [{ field, dir }],
        skip: 0,
      });
    },
    [dataState, onDataStateChange]
  );

  return (
    <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-2 gap-y-2 bg-[#233843] px-2 py-1">
      <SearchResultsToolbarActions
        rightContent={
          <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
            <label
              htmlFor="search-sort-by-dropdown"
              className="text-white text-sm whitespace-nowrap"
            >
              {t('Sort by')}
            </label>
            <DropDownList
              id="search-sort-by-dropdown"
              className="k-picker-solid product-filter-dropdown"
              style={{ width: 200 }}
              data={translatedSortOptions}
              textField="label"
              dataItemKey="value"
              value={selectedSort}
              onChange={handleSortChange}
            />
          </div>
        }
      />
    </div>
  );
};

export default SearchCardGridHeader;
